import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Multiselect, Select, SpaceBetween, Box } from '@cloudscape-design/components';
import { FilterOption } from '@amzn/controllershiphub-typescript-client';
import { PeriodFilter } from './PeriodFilter';
import './filterstyles.scss';

const SELECT_ALL_VALUE = 'all';

const FILTER_ORDER = [
  'Period Name',
  'Region',
  'Ledger Name',
  'Country Name',
  'Location Name',
  'Location Type',
  'Company Code',
  'Cost Center',
  'Account Number',
  'GL Posted Date',
];

const REQUIRED_FILTERS = [
  'Period Name',
  'Region',
  'Ledger Name',
  'Country Name',
  'Cost Center',
  'Account Number',
  'Account',
  'Accrual Period',
];

const getFilterOrder = (filterName: string): number => {
  const index = FILTER_ORDER.indexOf(filterName);
  return index === -1 ? FILTER_ORDER.length : index;
};

const getSelectAllOption = () => ({
  label: 'Select All',
  value: SELECT_ALL_VALUE,
});

interface FilterPanelProps {
  availableFilters: Record<string, FilterOption>;
  filterValues: Record<string, string[]>;
  onFiltersUpdate: (filters: Record<string, string[]>) => void;
  currentFilters: Record<string, string[]>;
  isLoading?: boolean;
  filterLoadingStates?: Record<string, boolean>;
  filterErrors?: Record<string, string | null>;
  onRefreshFilter?: (filterName: string) => void;
  dataType?: 'GL' | 'PO' | 'DEFAULT';
  controlId?: string;
}

const isFilterEnabled = (
  filterName: string,
  selectedFilters: Record<string, string[]>,
  isControl18956: boolean,
  isGLData: boolean
): boolean => {
  // Period Name is always enabled for control 18956 GL data
  if (filterName === 'Period Name') {
    return isControl18956 && isGLData;
  }

  // Region is always enabled for control 18956 GL data
  if (filterName === 'Region') {
    return isControl18956 && isGLData;
  }

  // For control 18956 GL data: Ledger Name and Country Name require both Period Name and Region
  if (
    isControl18956 &&
    isGLData &&
    (filterName === 'Ledger Name' || filterName === 'Country Name')
  ) {
    return !!(selectedFilters['Period Name']?.length && selectedFilters['Region']?.length);
  }

  // For control 18956 GL data: All other filters require Ledger Name and Country Name
  if (isControl18956 && isGLData) {
    return !!(selectedFilters['Ledger Name']?.length && selectedFilters['Country Name']?.length);
  }

  // For other controls, all filters are enabled by default
  return true;
};

export const FilterPanel: React.FC<FilterPanelProps> = ({
  availableFilters,
  filterValues,
  onFiltersUpdate,
  currentFilters,
  isLoading,
  filterLoadingStates = {},
  dataType = 'DEFAULT',
  controlId,
}) => {
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>(currentFilters);

  const isGLData = dataType === 'GL';
  const isPOData = dataType === 'PO';
  const isControl18956 = controlId?.startsWith('grc18956') || false;

  // Debug logging
  console.log('FilterPanel Debug:', { dataType, controlId, isGLData, isPOData, isControl18956 });

  const memoizedFilterValues = useMemo(() => filterValues, [filterValues]);

  const handleFilterChange = useCallback(
    (filterName: string, values: string[]) => {
      const updatedFilters = {
        ...selectedFilters,
        [filterName]: values,
      };
      setSelectedFilters(updatedFilters);
      onFiltersUpdate(updatedFilters);
    },
    [selectedFilters, onFiltersUpdate]
  );

  const handleFilterChangeWithSelectAll = useCallback(
    (filterName: string, values: string[]) => {
      if (filterName !== 'Period Name') {
        const availableOptions = memoizedFilterValues[filterName] || [];
        const currentSelection = selectedFilters[filterName] || [];
        const wasSelectAllSelected = currentSelection.includes(SELECT_ALL_VALUE);
        const isSelectAllSelected = values.includes(SELECT_ALL_VALUE);

        if (isSelectAllSelected && !wasSelectAllSelected) {
          const allValues = [SELECT_ALL_VALUE, ...availableOptions];
          handleFilterChange(filterName, allValues);
        } else if (!isSelectAllSelected && wasSelectAllSelected) {
          handleFilterChange(filterName, []);
        } else {
          const filteredValues = values.filter((v) => v !== SELECT_ALL_VALUE);
          handleFilterChange(filterName, filteredValues);
        }
      } else {
        handleFilterChange(filterName, values);
      }
    },
    [selectedFilters, memoizedFilterValues, handleFilterChange]
  );

  useEffect(() => {
    setSelectedFilters(currentFilters);
  }, [currentFilters]);

  return (
    <div className="filter-panel">
      {/* Period Name filter is hidden for GRC18956 GL data - auto-populated in ControlFilterView */}

      {/* Render other filters in specified order */}
      {Object.entries(availableFilters)
        .filter(([filterName]) => {
          // Hide Region filter for non-18956 controls
          if (filterName === 'Region' && !isControl18956) {
            return false;
          }
          return true;
        })
        .sort(([a], [b]) => getFilterOrder(a) - getFilterOrder(b))
        .map(([filterName, option]) => {
          const selectedOptions = Array.isArray(selectedFilters[filterName])
            ? selectedFilters[filterName].map((value) => ({
                label: value === SELECT_ALL_VALUE ? 'Select All' : value,
                value,
              }))
            : [];

          const availableOptions = memoizedFilterValues[filterName] || [];
          const isCostCenterColumn =
            filterName.toLowerCase().includes('cost') &&
            filterName.toLowerCase().includes('center');
          const options =
            filterName === 'Period Name' || isCostCenterColumn
              ? availableOptions.map((value) => ({ label: value, value }))
              : [
                  getSelectAllOption(),
                  ...availableOptions.map((value) => ({ label: value, value })),
                ];

          const enabled = isFilterEnabled(filterName, selectedFilters, isControl18956, isGLData);

          // Use PeriodFilter for period-related filters only in GL data AND control 18956
          if (isGLData && isControl18956 && filterName.toLowerCase().includes('period')) {
            const isRequired = REQUIRED_FILTERS.some((req) =>
              filterName.toLowerCase().includes(req.toLowerCase())
            );
            return (
              <div key={filterName} className="filter-panel__item">
                <div className="filter-panel__label">
                  {filterName} {isRequired && <span style={{ color: 'red' }}>*</span>}
                </div>
                <PeriodFilter
                  selectedPeriods={selectedFilters[filterName] || []}
                  onPeriodsChange={(periods) => handleFilterChange(filterName, periods)}
                  isLoading={isLoading || filterLoadingStates[filterName]}
                  disabled={!enabled}
                />
              </div>
            );
          }

          // Show required indicators only for GL data AND control 18956
          const isRequired =
            isControl18956 &&
            REQUIRED_FILTERS.some((req) => filterName.toLowerCase().includes(req.toLowerCase()));

          const isSingleSelect = dataType === 'PO' && filterName.toLowerCase() === 'accrual period';

          return (
            <div key={filterName} className="filter-panel__item">
              <div className="filter-panel__label">
                {filterName} {isRequired && <span style={{ color: 'red' }}>*</span>}
              </div>
              {isSingleSelect ? (
                <Select
                  selectedOption={selectedOptions.length > 0 ? selectedOptions[0] : null}
                  onChange={({ detail }) => {
                    const newValue = detail.selectedOption?.value;
                    handleFilterChangeWithSelectAll(filterName, newValue ? [newValue] : []);
                  }}
                  options={options.filter((opt) => opt.value !== SELECT_ALL_VALUE)}
                  filteringType="auto"
                  placeholder={`Select ${filterName}`}
                  disabled={!enabled || isLoading || filterLoadingStates[filterName]}
                />
              ) : (
                <Multiselect
                  selectedOptions={selectedOptions}
                  onChange={({ detail }) => {
                    const newValues = detail.selectedOptions
                      .map((o) => o.value)
                      .filter((value): value is string => value !== undefined);
                    handleFilterChangeWithSelectAll(filterName, newValues);
                  }}
                  options={options}
                  filteringType="auto"
                  placeholder={`Select ${filterName}`}
                  selectedAriaLabel="Selected"
                  disabled={!enabled || isLoading || filterLoadingStates[filterName]}
                  tokenLimit={5}
                  virtualScroll
                />
              )}
            </div>
          );
        })}
    </div>
  );
};
